"use client";

import { useRef, useState, type FC, type PointerEvent as ReactPointerEvent } from "react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import * as Dialog from "@radix-ui/react-dialog";
import { Button } from "@/components/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/cn";
import type { DrawerState } from "./drawer-provider";

type DrawerProps = {
  state: DrawerState;
  onDismiss: () => void;
  onExited: () => void;
};

/* Figma 1052:63699. Padding is per-side and asymmetric in the design — pl 32 / pr 24.
   `md:` throughout: below that the panel is a bottom sheet, so `side` doesn't apply. */
const sideClasses = {
  right:
    "md:right-0 md:rounded-l-large md:pl-32 md:pr-24 md:data-[state=closed]:animate-[drawer-slide-out_250ms_ease-in] md:data-[state=open]:animate-[drawer-slide-in_250ms_ease-out]",
  left: "md:left-0 md:rounded-r-large md:pr-32 md:pl-24 md:data-[state=closed]:animate-[sidebar-slide-out_250ms_ease-in] md:data-[state=open]:animate-[sidebar-slide-in_250ms_ease-out]",
} as const;

const SHEET_MIN = 0.3;
const SHEET_MAX = 0.95;
const SHEET_DISMISS = 0.2;

/**
 * The one drawer in the app, mounted by `DrawerProvider`. Strictly layout: it draws the scrim,
 * panel, title row and divider, then hands the rest of the panel to `state.content`. Buttons,
 * forms and pending state belong to the content — see spec §4.1.
 */
export const Drawer: FC<DrawerProps> = ({ state, onDismiss, onExited }) => {
  const { isOpen, content, options } = state;

  const isMobile = useIsMobile();
  const panelRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startY: number; startHeight: number } | null>(null);
  const [sheetHeight, setSheetHeight] = useState<number | null>(null);

  const draggedHeight = (event: ReactPointerEvent) => {
    const drag = dragRef.current;
    return drag ? drag.startHeight + drag.startY - event.clientY : 0;
  };

  const onDragStart = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!isMobile || !panelRef.current) return;
    dragRef.current = { startY: event.clientY, startHeight: panelRef.current.getBoundingClientRect().height };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onDragMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const { innerHeight } = window;
    setSheetHeight(Math.min(Math.max(draggedHeight(event), innerHeight * SHEET_MIN), innerHeight * SHEET_MAX));
  };

  const onDragEnd = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const shouldDismiss = draggedHeight(event) < window.innerHeight * SHEET_DISMISS;
    dragRef.current = null;
    if (shouldDismiss) onDismiss();
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(next) => {
        /* Esc, scrim click and the X glyph all land here, so the guard is checked in one place. */
        if (!next) onDismiss();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-neutral-black/30 backdrop-blur-sm backdrop-saturate-150 data-[state=closed]:animate-[fade-out_200ms_ease-in] data-[state=open]:animate-[fade-in_200ms_ease-out]" />
        <Dialog.Content
          ref={panelRef}
          style={
            isMobile && sheetHeight !== null ? { height: sheetHeight, maxHeight: `${SHEET_MAX * 100}dvh` } : undefined
          }
          /* Radix keeps the panel mounted for the duration of the exit animation, so this is the
             point at which it is safe to drop the content — which unmounts it and resets any form
             state inside for the next open. */
          onAnimationEnd={() => {
            if (isOpen) return;
            setSheetHeight(null);
            onExited();
          }}
          className={cn(
            "fixed z-50 flex w-full flex-col bg-neutral-white px-16 py-24 outline-none",
            /* Mobile: a bottom sheet. `h-full` against the cap fixes it at 85dvh whatever the
               content height, leaving the rest of the scrim reachable above it. */
            "inset-x-0 bottom-0 h-full max-h-[70dvh] rounded-t-large",
            "max-md:data-[state=closed]:animate-[sheet-slide-out_250ms_ease-in] max-md:data-[state=open]:animate-[sheet-slide-in_250ms_ease-out]",
            /* md and up: the full-height side panel. */
            "md:inset-x-auto md:inset-y-0 md:h-dvh md:max-h-none md:w-730 md:rounded-t-none md:px-32",
            sideClasses[options.side ?? "right"],
            options.className,
          )}
        >
          <div className="flex min-h-0 flex-1 flex-col">
            <div
              className="flex flex-col gap-16 max-md:-mt-24 max-md:cursor-grab max-md:touch-none max-md:select-none max-md:pt-24"
              onPointerDown={onDragStart}
              onPointerMove={onDragMove}
              onPointerUp={onDragEnd}
              onPointerCancel={onDragEnd}
            >
              <div aria-hidden className="-mt-12 flex justify-center pb-4 md:hidden">
                <span className="h-4 w-40 rounded-circle bg-neutral-black/20" />
              </div>
              <div className="flex items-center justify-between gap-16">
                <Dialog.Title className="text-24 leading-[1.3] font-semibold text-neutral-black">
                  {options.title}
                </Dialog.Title>
                {/* Negative margin so the Button's 38px hit area grows outwards and the glyph
                    stays on the design's grid. Same treatment as components/alert-dialog. */}
                <Dialog.Close asChild>
                  <Button
                    type="button"
                    variant="icon"
                    size="s"
                    aria-label="Close"
                    className="-m-8 shrink-0"
                    icon={<XMarkIcon aria-hidden className="size-24" />}
                  />
                </Dialog.Close>
              </div>
              {/* Always rendered: Radix warns when a dialog has no description. */}
              <Dialog.Description className="sr-only">
                {options.description ?? "Press Escape to close this panel."}
              </Dialog.Description>
              <hr className="border-neutral-light-grey" />
            </div>

            {/* `[&>div]:h-full` on top of the base `[&>div]:!block` (see scroll-area.tsx): Radix's
                viewport wraps children in an auto-height div, which would otherwise break the
                `min-h-full` chain the footer pattern in spec §4.6 depends on. Explicit `height`
                (not `min-height`) is required for that div to count as a percentage basis for its
                own children — content taller than 100% still overflows it visually and is still
                picked up by the viewport's scrollHeight, since this div stays `overflow: visible`. */}
            <ScrollArea className="min-h-0 flex-1" viewportClassName="pt-32 [&>div]:h-full pr-8">
              {content}
            </ScrollArea>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
